// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';

import { getStaticsBaseUrl } from '@oysterjs/core/config';

import { scopeCSS } from './editor-css';
import { parseFullHTML } from '~/components/document-editor/template';

/**
 * Inject scoped CSS into the editor root: document `<head>` styles first, then
 * the backend stylesheet (Styles tab / statics.go) so preview matches PDF order.
 */
export function useEditorStylesheets(params: {
  editorRootRef: React.RefObject<HTMLDivElement | null>;
  content: string;
  scopeId: string;
  workspaceId?: string;
  artifactId?: string;
  artifactVersion?: number;
  templateId?: string;
  showPreview?: boolean;
}) {
  const {
    editorRootRef,
    content,
    scopeId,
    workspaceId,
    artifactId,
    artifactVersion,
    templateId,
    showPreview
  } = params;

  React.useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;
    const parsed = parseFullHTML(content);
    const headStyles = parsed?.headStyles ?? '';
    if (!headStyles.trim()) return;
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-editor-head-styles', 'true');
    styleEl.textContent = scopeCSS(headStyles, scopeId);
    root.prepend(styleEl);
    return () => styleEl.remove();
  }, [content, artifactId, artifactVersion, editorRootRef, scopeId, showPreview]);

  React.useEffect(() => {
    const root = editorRootRef.current;
    if (!root || !workspaceId || !artifactId) return;

    const urlParams = new URLSearchParams();
    if (templateId) urlParams.set('style_id', templateId);
    if (artifactVersion !== undefined) urlParams.set('version', String(artifactVersion));
    const qs = urlParams.toString() ? `?${urlParams.toString()}` : '';
    const url = `${getStaticsBaseUrl()}/agency/html/${workspaceId}/${artifactId}/stylesheet${qs}`;

    let cancelled = false;
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-editor-backend-styles', 'true');

    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((css) => {
        if (cancelled || !css) return;
        const inner = css.replace(/<\/?style[^>]*>/gi, '').trim();
        if (!inner) return;
        styleEl.textContent = scopeCSS(inner, scopeId);
        root.appendChild(styleEl);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      styleEl.remove();
    };
  }, [artifactId, artifactVersion, editorRootRef, scopeId, templateId, workspaceId]);
}
